import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/lib/roles";
import { RefreshCw, Loader2, LayoutTemplate, Eye, EyeOff, Link2, Star, Archive, RotateCcw } from "lucide-react";

const STATUS_STYLE = {
  approved: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  rejected: "bg-red-100 text-red-700",
  disabled: "bg-zinc-100 text-zinc-500",
};

const KIND_OPTIONS = [
  { value: "", label: "— (sem tipo)" },
  { value: "convite", label: "Convite" },
  { value: "confirmacao", label: "Confirmação" },
  { value: "cancelamento", label: "Cancelamento" },
  { value: "dispensa", label: "Dispensa" },
  { value: "nao_selecionado", label: "Não selecionado" },
  { value: "recibo_lembrete", label: "Recibo — 1º aviso" },
  { value: "recibo_lembrete_2", label: "Recibo — 2º aviso" },
  { value: "fardamento", label: "Fardamento" },
  { value: "reengagement", label: "Reengagement (reabrir conversa na Inbox)" },
  { value: "lembrete_evento", label: "Lembrete do evento (véspera, só confirmados)" },
];

export default function MetaTemplatesSyncCard() {
  const { isAdmin } = useRole();
  const [wa, setWa] = useState(null);
  const [rows, setRows] = useState([]);
  const [promoTemplates, setPromoTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [openPreview, setOpenPreview] = useState({});
  const [showHidden, setShowHidden] = useState(false);

  const loadWa = () => api.get("/organizations/me/whatsapp-settings").then(({ data }) => setWa(data)).catch(() => {});
  const loadRows = (sh = showHidden) => api.get(`/wa-templates${sh ? "?include_hidden=true" : ""}`).then(({ data }) => setRows(data || [])).catch(() => {});
  const loadPromo = () => api.get("/templates").then(({ data }) => setPromoTemplates(data || [])).catch(() => {});
  useEffect(() => { loadWa(); loadRows(); loadPromo(); }, []);

  if (!wa) return null;
  if ((wa.whatsapp_provider || "meta_cloud") !== "meta_cloud") return null;

  const sync = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/organizations/me/whatsapp/meta-sync-templates");
      setMsg({ type: "ok", text: `Sincronizados ${data.count} template(s) da Meta.` });
      await loadRows();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha ao sincronizar." });
    } finally { setBusy(false); }
  };

  const linkTemplate = async (id, messageTemplateId) => {
    try {
      const { data } = await api.put(`/wa-templates/${id}/link`, { message_template_id: messageTemplateId || null });
      setRows(rows.map((r) => (r.id === id ? { ...r, message_template_id: messageTemplateId || null, kind: data.kind } : r)));
      setMsg({ type: "ok", text: "Associação guardada." });
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha ao associar." });
    }
  };

  const setKind = async (id, kind) => {
    try {
      await api.put(`/wa-templates/${id}/kind`, { kind: kind || null });
      setRows(rows.map((r) => (r.id === id ? { ...r, kind: kind || null, active: false } : r)));
      setMsg({ type: "ok", text: "Tipo atualizado. Define o predefinido para este tipo se necessário." });
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha ao atualizar tipo." });
    }
  };

  const setHidden = async (id, hidden) => {
    try {
      await api.put(`/wa-templates/${id}/hidden`, { hidden });
      await loadRows();
      setMsg({ type: "ok", text: hidden ? "Template ocultado da lista." : "Template reposto na lista." });
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha ao atualizar." });
    }
  };

  const toggleShowHidden = () => {
    const next = !showHidden;
    setShowHidden(next);
    loadRows(next);
  };

  const setDefault = async (row) => {
    try {
      await api.put(`/wa-templates/${row.id}/default`);
      setRows(rows.map((r) => (r.kind === row.kind ? { ...r, active: r.id === row.id } : r)));
      setMsg({ type: "ok", text: `"${row.name}" definido como predefinido para "${row.kind}".` });
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha ao definir predefinido." });
    }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-4" data-testid="meta-templates-sync-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-zinc-950 text-white flex items-center justify-center"><LayoutTemplate className="w-4.5 h-4.5" /></div>
          <div>
            <div className="font-display text-lg font-semibold text-zinc-950">Templates aprovados da Meta</div>
            <div className="text-xs text-zinc-500">Associa cada template Meta a um template da PromoCrew (obrigatório para iniciar conversas)</div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer" data-testid="meta-show-hidden-toggle">
              <input type="checkbox" checked={showHidden} onChange={toggleShowHidden} className="rounded border-zinc-300" />
              Mostrar ocultos
            </label>
            <button onClick={sync} disabled={busy} data-testid="meta-sync-templates-btn"
              className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizar da Meta
            </button>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-zinc-500" data-testid="meta-templates-empty">
          Ainda não há templates sincronizados. Carrega em <strong>Sincronizar da Meta</strong> para importar os teus templates aprovados.
        </div>
      ) : (
        <div className="space-y-2" data-testid="meta-templates-list">
          {rows.map((r) => {
            const linked = promoTemplates.find((t) => t.id === r.message_template_id);
            const isOpen = !!openPreview[r.id];
            return (
              <div key={r.id} className={`border border-zinc-100 rounded-lg px-3 py-2.5 space-y-2 ${r.hidden ? "opacity-60 bg-zinc-50" : ""}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate flex items-center gap-2">
                      {r.name}
                      {r.hidden && <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-zinc-200 text-zinc-600">oculto</span>}
                      <span className="text-[11px] text-zinc-400 font-normal">{r.language} · {r.param_count} var.</span>
                    </div>
                    <div className="text-[11px] text-zinc-400">{r.category || "—"}{linked ? ` · associado a "${linked.name}"` : " · sem associação"}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[r.status] || "bg-zinc-100 text-zinc-600"}`}>{r.status}</span>
                    {r.status === "approved" && r.kind && !r.hidden && (
                      <button type="button" onClick={() => setDefault(r)} disabled={!isAdmin || r.active}
                        data-testid={`meta-template-default-${r.name}`}
                        title={r.active ? "Predefinido para este tipo" : "Definir como predefinido para este tipo"}
                        className={`inline-flex items-center gap-1 text-xs rounded-lg px-2 py-1.5 border transition-colors ${r.active ? "border-amber-300 bg-amber-50 text-amber-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-60"}`}>
                        <Star className={`w-3.5 h-3.5 ${r.active ? "fill-amber-400 text-amber-500" : ""}`} />
                        {r.active ? "Predefinido" : "Definir predefinido"}
                      </button>
                    )}
                    <button type="button" onClick={() => setOpenPreview({ ...openPreview, [r.id]: !isOpen })}
                      data-testid={`meta-template-preview-toggle-${r.name}`}
                      className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-lg px-2 py-1.5">
                      {isOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} Pré-visualizar
                    </button>
                    {isAdmin && (
                      r.hidden ? (
                        <button type="button" onClick={() => setHidden(r.id, false)}
                          data-testid={`meta-template-restore-${r.name}`}
                          title="Repor na lista"
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 border border-emerald-200 rounded-lg px-2 py-1.5 hover:bg-emerald-50">
                          <RotateCcw className="w-3.5 h-3.5" /> Repor
                        </button>
                      ) : (
                        <button type="button" onClick={() => setHidden(r.id, true)}
                          data-testid={`meta-template-hide-${r.name}`}
                          title="Ocultar da lista (não apaga na Meta)"
                          className="inline-flex items-center gap-1 text-xs text-zinc-500 border border-zinc-200 rounded-lg px-2 py-1.5 hover:bg-zinc-50">
                          <Archive className="w-3.5 h-3.5" /> Ocultar
                        </button>
                      )
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 space-y-2" data-testid={`meta-template-preview-${r.name}`}>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Corpo aprovado na Meta</div>
                    <div className="text-xs text-zinc-800 whitespace-pre-wrap font-mono">{r.body_preview || <span className="text-zinc-400 font-sans">(sem corpo de texto)</span>}</div>
                    <div className="text-[11px] text-zinc-500">Espera <strong>{r.param_count}</strong> parâmetro(s). O template PromoCrew associado deve gerar exatamente as mesmas variáveis, na mesma ordem.</div>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Link2 className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="text-xs text-zinc-500">Tipo:</span>
                  <select value={r.kind || ""} disabled={!isAdmin}
                    onChange={(e) => setKind(r.id, e.target.value)}
                    data-testid={`meta-template-kind-${r.name}`}
                    className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white disabled:opacity-60 min-w-[150px]">
                    {KIND_OPTIONS.map((k) => (
                      <option key={k.value} value={k.value}>{k.label}</option>
                    ))}
                  </select>
                  <span className="text-xs text-zinc-500 ml-1">Associar a template PromoCrew:</span>
                  <select value={r.message_template_id || ""} disabled={!isAdmin}
                    onChange={(e) => linkTemplate(r.id, e.target.value)}
                    data-testid={`meta-template-link-${r.name}`}
                    className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white disabled:opacity-60 min-w-[180px]">
                    <option value="">— (não associado)</option>
                    {promoTemplates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}{t.kind ? ` (${t.kind})` : ""}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-zinc-400 pt-1">Só templates <strong>approved</strong> e <strong>associados</strong> são usados no envio. Sem associação, o convite é bloqueado (não sai como texto livre). O <strong>reenvio automático às 24h</strong> a quem não respondeu usa sempre o template de <strong>Convite</strong>. O <strong>lembrete da véspera</strong> (só para confirmados) usa o tipo <strong>Lembrete do evento</strong>. O tipo <strong>Reengagement</strong> serve apenas para reabrir conversas na Inbox.</p>
        </div>
      )}

      {msg && (
        <div className={`text-xs ${msg.type === "ok" ? "text-emerald-600" : "text-red-600"}`} data-testid="meta-sync-msg">{msg.text}</div>
      )}
    </div>
  );
}
